import React from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import SEO from "../components/seo";
import SiteLayout from "../components/site/SiteLayout";
import EditLink from "../components/site/EditLink";
import RichText, { MultilineText } from "../components/site/RichText";
import { Icon, IcoMedal, IcoLeaf, IcoCycle } from "../components/site/icons";
import { url as apiUrl } from "../api";
import { toLines, mediaUrl } from "../lib/content";
import { usePbqh } from "../lib/pbqh";
import pbqphLogo from "../images/pbqph-logo.png";

function IconCardGrid({ items }) {
  return (
    <div className="icon-card-grid">
      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="icon-card" key={item.id || `${item.titulo}-${index}`}>
          <span className="icon-card-badge">
            <Icon name={item.icone} />
          </span>
          <h3 className="icon-card-title">{item.titulo}</h3>
          <p className="icon-card-text">{item.texto}</p>
        </div>
      ))}
    </div>
  );
}

IconCardGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      icone: PropTypes.string,
      titulo: PropTypes.string,
      texto: PropTypes.string,
    })
  ),
};

export default function Pbqh() {
  const page = usePbqh();
  const selo = mediaUrl(apiUrl, page.selo);
  const seloRodape = mediaUrl(apiUrl, page.seloRodape) || pbqphLogo;

  return (
    <SiteLayout>
      <SEO
        lang="pt-BR"
        keywords={[`Bivver`, `PBQP-H`, `SiAC`, `qualidade`, `certificação`]}
        description="Política da Qualidade da Bivver Empreendimentos e certificação PBQP-H (Programa Brasileiro de Qualidade e Produtividade do Habitat)."
        title="Qualidade & PBQP-H — Bivver Empreendimentos"
      />

      <EditLink to="/edicao/pbqh" />

      <section className="qual-hero">
        {selo && <img className="qual-hero-selo" src={selo} alt="Selo PBQP-H" />}
        <span className="institutional-eyebrow qual-hero-eyebrow">
          {page.eyebrow}
        </span>
        <h1 className="qual-hero-title">{page.titulo}</h1>
        <p className="qual-hero-sub">{page.subtitulo}</p>
        <div className="qual-hero-chips">
          {page.chips.map((chip, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <span className="qual-hero-chip" key={chip.id || index}>
              <Icon name={chip.icone} /> {chip.texto}
            </span>
          ))}
        </div>
      </section>

      <section className="institutional-page qual-page">
        <div className="institutional-intro">
          <RichText source={page.intro} />
        </div>

        <h2 className="qual-h2">{page.tituloPolitica}</h2>
        <p className="qual-lead">{page.leadPolitica}</p>
        <IconCardGrid items={page.pilares} />

        <div className="qual-band">
          <h2 className="qual-h2">
            <IcoMedal /> {page.tituloCertificacao}
          </h2>
          <div className="institutional-intro">
            <RichText source={page.introCertificacao} />
          </div>
          <p className="qual-lead">{page.leadCertificacao}</p>
          <IconCardGrid items={page.beneficios} />
        </div>

        {page.citacao && (
          <blockquote className="institutional-highlight qual-quote">
            <MultilineText text={page.citacao} />
          </blockquote>
        )}

        <div className="qual-compromisso">
          <span className="qual-compromisso-label">
            {page.compromissoLabel}
          </span>
          <p>{page.compromisso}</p>
        </div>

        <h2 className="qual-h2">{page.tituloFases}</h2>
        <p className="qual-lead">{page.leadFases}</p>
        <div className="fases-grid">
          {page.fases.map((fase, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="fase-card" key={fase.id || index}>
              <span className="fase-num">{index + 1}</span>
              <h3 className="fase-title">{fase.titulo}</h3>
              <p className="fase-intro">{fase.descricao}</p>
              <ul className="fase-bullets">
                {toLines(fase.itens).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="qual-pdca">
          <span className="qual-pdca-icon">
            <IcoCycle />
          </span>
          <div>
            <h2 className="qual-h2 qual-h2-left">{page.tituloPdca}</h2>
            <p className="qual-kicker qual-kicker-left">{page.kickerPdca}</p>
            <div className="qual-pdca-text">
              <RichText source={page.textoPdca} />
            </div>
          </div>
        </div>

        <div className="qual-band qual-band-green">
          <h2 className="qual-h2">
            <IcoLeaf /> {page.tituloSustentabilidade}
          </h2>
          <p className="qual-kicker">{page.kickerSustentabilidade}</p>
          <div className="institutional-intro">
            <RichText source={page.introSustentabilidade} />
          </div>
          <IconCardGrid items={page.praticas} />
        </div>

        <blockquote className="qual-closing">
          <p>{page.citacaoFinal}</p>
          <span className="qual-closing-brand">{page.marcaFinal}</span>
          <span className="qual-closing-tag">{page.tagFinal}</span>
        </blockquote>

        <div className="qual-cta-wrap">
          <Link className="home-hero-cta" to="/contato">
            {page.ctaTexto}
          </Link>
        </div>

        {seloRodape && (
          <footer className="qual-rodape">
            <img
              className="qual-rodape-selo"
              src={seloRodape}
              alt="Programa Brasileiro da Qualidade e Produtividade do Habitat (PBQP-H)"
            />
            {page.seloRodapeLegenda && (
              <span className="qual-rodape-legenda">
                {page.seloRodapeLegenda}
              </span>
            )}
          </footer>
        )}
      </section>
    </SiteLayout>
  );
}
